/**
 * PAY-01C: AR Payment Plan Workspace
 * Route: /ar/payment-plans  (ar.plan.view)
 *
 * Displays summary cards + plan list.
 * Create Plan builder with MDM template selection.
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, RefreshCw, ChevronRight, CalendarRange, DollarSign,
  AlertTriangle, CheckCircle2, Clock, Archive, ClipboardList,
  TrendingUp, Bell,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { arPayPlanApi, arReceiptApi, formatError } from "@/services/api";

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmt(minor, currency = "INR") {
  if (minor == null) return "—";
  return new Intl.NumberFormat("en-IN", { style: "currency", currency, minimumFractionDigits: 0 }).format(minor / 100);
}
function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

const PLAN_STATUS_CFG = {
  DRAFT:      { label: "Draft",     color: "#64748B", bg: "#F1F5F9", Icon: Clock },
  ACTIVE:     { label: "Active",    color: "#1D4ED8", bg: "#EFF6FF", Icon: TrendingUp },
  COMPLETED:  { label: "Completed", color: "#166534", bg: "#F0FDF4", Icon: CheckCircle2 },
  CANCELLED:  { label: "Cancelled", color: "#991B1B", bg: "#FEF2F2", Icon: Archive },
};

const INST_STATUS_COLOR = {
  SCHEDULED:     "#64748B",
  DUE:           "#D97706",
  INVOICE_RAISED: "#2563EB",
  PARTIALLY_PAID: "#7C3AED",
  PAID:          "#166534",
  OVERDUE:       "#DC2626",
  WAIVED:        "#64748B",
  CANCELLED:     "#9CA3AF",
};

function StatusBadge({ status, cfg }) {
  const c = cfg || { label: status, color: "#64748B", bg: "#F1F5F9" };
  return (
    <span style={{ background: c.bg, color: c.color, border: `1px solid ${c.color}30`,
      borderRadius: 9999, padding: "2px 10px", fontSize: 11, fontWeight: 600 }}>
      {c.label || status}
    </span>
  );
}

function SummaryCard({ label, value, sub, color, Icon }) {
  return (
    <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12,
      padding: "18px 20px", display: "flex", flexDirection: "column", gap: 4 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 12, color: "#64748B", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
          {label}
        </span>
        {Icon && <Icon size={16} color={color || "#64748B"} />}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: color || "#0F172A" }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "#94A3B8" }}>{sub}</div>}
    </div>
  );
}

// ── Installment Row Builder ───────────────────────────────────────────────────
function InstallmentBuilder({ installments, onChange }) {
  const addRow = () => {
    const next = installments.length + 1;
    onChange([...installments, {
      installment_number: next,
      milestone_label: "",
      milestone_type: "ADVANCE",
      scheduled_amount_minor: 0,
      due_date: "",
      notes: "",
    }]);
  };
  const removeRow = (idx) => onChange(installments.filter((_, i) => i !== idx));
  const updateRow = (idx, field, value) => {
    const updated = [...installments];
    updated[idx] = { ...updated[idx], [field]: value };
    onChange(updated);
  };

  const total = installments.reduce((s, i) => s + Number(i.scheduled_amount_minor || 0), 0);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <span style={{ fontWeight: 600, fontSize: 14, color: "#0F172A" }}>
          Installment Schedule
        </span>
        <button onClick={addRow} data-testid="add-installment-btn"
          style={{ display: "flex", alignItems: "center", gap: 4, background: "#1E293B", color: "#fff",
            border: "none", borderRadius: 6, padding: "5px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
          <Plus size={12} /> Add
        </button>
      </div>

      {installments.length === 0 && (
        <div style={{ textAlign: "center", padding: "24px 0", color: "#94A3B8", fontSize: 13 }}>
          No installments yet. Click Add to build the schedule.
        </div>
      )}

      {installments.map((inst, idx) => (
        <div key={idx} style={{ border: "1px solid #E2E8F0", borderRadius: 8, padding: "12px 14px",
          marginBottom: 8, background: "#FAFAFA" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr 1fr 1fr auto", gap: 8, alignItems: "end" }}>
            <div>
              <label style={{ fontSize: 11, color: "#64748B", fontWeight: 600 }}>Type</label>
              <select value={inst.milestone_type}
                onChange={e => updateRow(idx, "milestone_type", e.target.value)}
                style={{ width: "100%", padding: "6px 8px", border: "1px solid #CBD5E1",
                  borderRadius: 6, fontSize: 12, marginTop: 2 }}>
                <option value="ADVANCE">Advance</option>
                <option value="BOOKING_ADVANCE">Booking Advance</option>
                <option value="INTERIM">Interim</option>
                <option value="FINAL_SETTLEMENT">Final Settlement</option>
                <option value="RETENTION">Retention</option>
                <option value="CUSTOM">Custom</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11, color: "#64748B", fontWeight: 600 }}>Label</label>
              <input value={inst.milestone_label}
                onChange={e => updateRow(idx, "milestone_label", e.target.value)}
                placeholder="e.g. Booking Deposit"
                data-testid={`inst-label-${idx}`}
                style={{ width: "100%", padding: "6px 8px", border: "1px solid #CBD5E1",
                  borderRadius: 6, fontSize: 12, marginTop: 2, boxSizing: "border-box" }} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: "#64748B", fontWeight: 600 }}>Amount (₹)</label>
              <input type="number" value={inst.scheduled_amount_minor / 100}
                onChange={e => updateRow(idx, "scheduled_amount_minor", Math.round(Number(e.target.value) * 100))}
                placeholder="0"
                data-testid={`inst-amount-${idx}`}
                style={{ width: "100%", padding: "6px 8px", border: "1px solid #CBD5E1",
                  borderRadius: 6, fontSize: 12, marginTop: 2, boxSizing: "border-box" }} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: "#64748B", fontWeight: 600 }}>Due Date</label>
              <input type="date" value={inst.due_date}
                onChange={e => updateRow(idx, "due_date", e.target.value)}
                data-testid={`inst-due-${idx}`}
                style={{ width: "100%", padding: "6px 8px", border: "1px solid #CBD5E1",
                  borderRadius: 6, fontSize: 12, marginTop: 2, boxSizing: "border-box" }} />
            </div>
            <button onClick={() => removeRow(idx)}
              style={{ background: "#FEF2F2", color: "#DC2626", border: "1px solid #FECACA",
                borderRadius: 6, padding: "6px 10px", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>
              ✕
            </button>
          </div>
        </div>
      ))}

      {installments.length > 0 && (
        <div style={{ textAlign: "right", paddingRight: 40, fontSize: 13, fontWeight: 700, color: "#0F172A", marginTop: 4 }}>
          Total: {fmt(total)}
        </div>
      )}
    </div>
  );
}

// ── Create Plan Modal ─────────────────────────────────────────────────────────
function CreatePlanModal({ onClose, onCreate }) {
  const [form, setForm] = useState({
    plan_name: "", customer_id: "", booking_id: "", legal_entity_id: "le-prashasta-events-001",
    currency_code: "INR", notes: "",
    reminder_config: { reminder_days_before: [7, 3, 1], channels: ["EMAIL"], enabled: true, suppress_if_paid: true },
  });
  const [installments, setInstallments] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    arPayPlanApi.getTemplates().then(r => setTemplates(r.data?.templates || [])).catch(() => {});
    arReceiptApi.listCustomerOutstanding && arReceiptApi.listCustomerOutstanding?.();
  }, []);

  const applyTemplate = (tpl) => {
    if (!tpl?.metadata?.percentages) return;
    const base = Number(form.total_amount || 0) * 100;
    const labels = tpl.metadata.labels || [];
    const pcts   = tpl.metadata.percentages || [];
    const today = new Date();
    const newInsts = pcts.map((pct, i) => {
      const due = new Date(today);
      due.setDate(due.getDate() + 30 * (i + 1));
      return {
        installment_number: i + 1,
        milestone_label: labels[i] || `Installment ${i + 1}`,
        milestone_type: i === 0 ? "BOOKING_ADVANCE" : (i === pcts.length - 1 ? "FINAL_SETTLEMENT" : "INTERIM"),
        scheduled_amount_minor: Math.round(base * pct / 100),
        due_date: due.toISOString().split("T")[0],
        notes: "",
      };
    });
    setInstallments(newInsts);
  };

  const handleSubmit = async () => {
    if (!form.plan_name.trim()) { toast.error("Plan name is required"); return; }
    if (!form.customer_id.trim()) { toast.error("Customer ID is required"); return; }
    if (!form.booking_id.trim()) { toast.error("Booking ID is required"); return; }
    if (installments.length === 0) { toast.error("Add at least one installment"); return; }
    for (const inst of installments) {
      if (!inst.milestone_label.trim()) { toast.error("All installment labels are required"); return; }
      if (!inst.due_date) { toast.error("All installment due dates are required"); return; }
      if (inst.scheduled_amount_minor <= 0) { toast.error("All installment amounts must be > 0"); return; }
    }
    setSaving(true);
    try {
      const res = await arPayPlanApi.create({ ...form, installments });
      toast.success(`Payment plan ${res.data.plan_number} created`);
      onCreate(res.data);
      onClose();
    } catch (e) {
      toast.error(formatError(e) || "Failed to create plan");
    } finally {
      setSaving(false);
    }
  };

  const inp = (field, placeholder, type = "text") => (
    <input type={type} value={form[field] || ""} placeholder={placeholder}
      onChange={e => setForm(p => ({ ...p, [field]: e.target.value }))}
      data-testid={`plan-${field}`}
      style={{ width: "100%", padding: "8px 12px", border: "1px solid #CBD5E1", borderRadius: 8,
        fontSize: 13, boxSizing: "border-box" }} />
  );

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 9999,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: "#fff", borderRadius: 16, maxWidth: 720, width: "100%",
        maxHeight: "92vh", overflow: "auto", padding: 28 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 20 }}>
          <h2 style={{ fontWeight: 700, fontSize: 18, color: "#0F172A", margin: 0 }}>
            Create Payment Plan
          </h2>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#64748B" }}>✕</button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4 }}>Plan Name *</label>
            {inp("plan_name", "e.g. Padma Singh Wedding Payment Plan")}
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4 }}>Customer ID *</label>
            {inp("customer_id", "Customer ID")}
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4 }}>Booking ID *</label>
            {inp("booking_id", "Booking reference ID")}
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4 }}>Total Contract Value (₹)</label>
            <input type="number" value={form.total_amount || ""} placeholder="e.g. 500000"
              onChange={e => setForm(p => ({ ...p, total_amount: e.target.value }))}
              data-testid="plan-total-amount"
              style={{ width: "100%", padding: "8px 12px", border: "1px solid #CBD5E1", borderRadius: 8,
                fontSize: 13, boxSizing: "border-box" }} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4 }}>Notes</label>
            {inp("notes", "Optional notes")}
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4 }}>Apply Template</label>
            <select data-testid="plan-template-select"
              onChange={e => {
                const tpl = templates.find(t => t.item_code === e.target.value);
                if (tpl) applyTemplate(tpl);
              }}
              style={{ width: "100%", padding: "8px 12px", border: "1px solid #CBD5E1",
                borderRadius: 8, fontSize: 13 }}>
              <option value="">— Select template —</option>
              {templates.map(t => <option key={t.item_code} value={t.item_code}>{t.display_name}</option>)}
            </select>
          </div>
        </div>

        <div style={{ marginBottom: 20 }}>
          <InstallmentBuilder installments={installments} onChange={setInstallments} />
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button onClick={onClose}
            style={{ padding: "9px 20px", border: "1px solid #CBD5E1", borderRadius: 8,
              background: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", color: "#374151" }}>
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={saving} data-testid="create-plan-submit-btn"
            style={{ padding: "9px 20px", border: "none", borderRadius: 8,
              background: saving ? "#94A3B8" : "#1E293B", color: "#fff",
              fontSize: 13, fontWeight: 600, cursor: saving ? "default" : "pointer" }}>
            {saving ? "Creating…" : "Create Plan"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Workspace ─────────────────────────────────────────────────────────────
export default function ARPaymentPlanWorkspace() {
  const navigate   = useNavigate();
  const { user }   = useAuth();
  const [plans, setPlans]         = useState([]);
  const [summary, setSummary]     = useState(null);
  const [loading, setLoading]     = useState(true);
  const [filterStatus, setFilter] = useState("");
  const [showCreate, setShowCreate] = useState(false);

  const canManage = user?.capabilities?.includes("ar.plan.manage");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [planRes, sumRes] = await Promise.all([
        arPayPlanApi.list(filterStatus ? { status: filterStatus } : {}),
        arPayPlanApi.getSummary(),
      ]);
      setPlans(planRes.data?.items || []);
      setSummary(sumRes.data);
    } catch (e) {
      toast.error(formatError(e) || "Failed to load payment plans");
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={{ padding: "28px 32px", maxWidth: 1200, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: "#0F172A", margin: 0 }}>
            Payment Plans
          </h1>
          <p style={{ fontSize: 13, color: "#64748B", marginTop: 2 }}>
            PAY-01C — Installment schedules, advance tracking, mock reminders
          </p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={load} data-testid="refresh-plans-btn"
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px",
              border: "1px solid #CBD5E1", borderRadius: 8, background: "#fff",
              fontSize: 13, fontWeight: 600, cursor: "pointer", color: "#374151" }}>
            <RefreshCw size={14} /> Refresh
          </button>
          {canManage && (
            <button onClick={() => setShowCreate(true)} data-testid="create-plan-btn"
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px",
                border: "none", borderRadius: 8, background: "#1E293B", color: "#fff",
                fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
              <Plus size={14} /> New Plan
            </button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 12, marginBottom: 24 }}>
          <SummaryCard label="Total Plans" value={summary.total_plans} Icon={ClipboardList} />
          <SummaryCard label="Active" value={summary.active} color="#1D4ED8" Icon={TrendingUp} />
          <SummaryCard label="Completed" value={summary.completed} color="#166534" Icon={CheckCircle2} />
          <SummaryCard label="Outstanding" value={fmt(summary.total_outstanding_minor)}
            color="#D97706" Icon={DollarSign}
            sub={`${fmt(summary.total_applied_minor)} collected`} />
          <SummaryCard label="Overdue" value={summary.overdue_installments} color="#DC2626" Icon={AlertTriangle}
            sub={`${summary.due_installments} due soon`} />
        </div>
      )}

      {/* Filter bar */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        {["", "DRAFT", "ACTIVE", "COMPLETED", "CANCELLED"].map(s => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`filter-${s || "all"}`}
            style={{ padding: "5px 14px", borderRadius: 9999, fontSize: 12, fontWeight: 600,
              cursor: "pointer", border: filterStatus === s ? "2px solid #1E293B" : "1px solid #CBD5E1",
              background: filterStatus === s ? "#1E293B" : "#fff",
              color: filterStatus === s ? "#fff" : "#374151" }}>
            {s || "All"}
          </button>
        ))}
      </div>

      {/* Plan list */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#94A3B8" }}>Loading payment plans…</div>
      ) : plans.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#94A3B8" }}>
          <CalendarRange size={40} style={{ margin: "0 auto 12px", display: "block" }} />
          <p style={{ fontSize: 15, fontWeight: 600, color: "#64748B" }}>No payment plans found</p>
          <p style={{ fontSize: 13 }}>
            {canManage ? "Click \"New Plan\" to create your first payment schedule." : "No plans have been created yet."}
          </p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {plans.map(plan => {
            const cfg = PLAN_STATUS_CFG[plan.status] || { label: plan.status, color: "#64748B", bg: "#F1F5F9" };
            return (
              <div key={plan.id} data-testid={`plan-row-${plan.id}`}
                onClick={() => navigate(`/ar/payment-plans/${plan.id}`)}
                style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12,
                  padding: "16px 20px", cursor: "pointer", display: "flex",
                  justifyContent: "space-between", alignItems: "center",
                  transition: "border-color 0.2s, box-shadow 0.2s" }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = "#94A3B8"; e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.07)"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "#E2E8F0"; e.currentTarget.style.boxShadow = "none"; }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontWeight: 700, fontSize: 14, color: "#0F172A" }}>
                      {plan.plan_name}
                    </span>
                    <StatusBadge status={plan.status} cfg={cfg} />
                  </div>
                  <div style={{ display: "flex", gap: 16, fontSize: 12, color: "#64748B" }}>
                    <span>{plan.plan_number}</span>
                    <span>Booking: {plan.booking_id?.slice(0, 8)}…</span>
                    <span>Created: {fmtDate(plan.created_at)}</span>
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: "#0F172A" }}>
                      {fmt(plan.total_scheduled_amount_minor)}
                    </div>
                    <div style={{ fontSize: 11, color: "#94A3B8" }}>scheduled</div>
                  </div>
                  <ChevronRight size={16} color="#94A3B8" />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showCreate && (
        <CreatePlanModal
          onClose={() => setShowCreate(false)}
          onCreate={() => load()}
        />
      )}
    </div>
  );
}
